import { createClient, type SupabaseClient } from '@supabase/supabase-js';

// We don't have a generated DB schema, so widen the SupabaseClient generics
// to `any` for the schema-scoped clients. The schema-scoped clients are
// typed against arbitrary schema names; the default `SupabaseClient` shape
// pins the schema parameter to `'public'`, which causes a TS2322 mismatch.
type AnyClient = SupabaseClient<any, any, any>;

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Whether the Supabase env vars are configured. Surfaced so the app shell
 * can render a clear "missing config" screen at boot instead of letting
 * the login form fail confusingly against an empty URL. main.tsx reads
 * this and short-circuits the React render.
 */
export const SUPABASE_CONFIGURED = Boolean(url && anonKey);

if (!SUPABASE_CONFIGURED) {
  console.warn(
    '[supabase] VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY missing. Copy .env.example to .env and fill in your Sports API Data Supabase credentials.',
  );
}

export const HARVEST_SCHEMA = import.meta.env.VITE_HARVEST_SCHEMA ?? 'harvest';
export const GENIUS_V2_SCHEMA = import.meta.env.VITE_GENIUS_V2_SCHEMA ?? 'genius_v2';
export const PRESTO_V2_SCHEMA = import.meta.env.VITE_PRESTO_V2_SCHEMA ?? 'presto_v2';
export const ENV_LABEL = import.meta.env.VITE_ENV_LABEL ?? 'local';

/**
 * Default client — points at the `public` schema. PostgREST exposes other
 * schemas only when explicitly requested via `db: { schema }` (below).
 */
export const supabase: AnyClient = createClient(url ?? '', anonKey ?? '', {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

/**
 * Token-forwarding fetch shared by every schema-scoped client. Each schema
 * client uses `persistSession: false` (we don't want three clients fighting
 * for the same auth storage key — that drives the GoTrueClient duplicate
 * warning), so they have no token of their own and borrow the bearer from
 * the default client per request.
 */
function makeAuthFetch(): typeof fetch {
  return ((input, init) =>
    supabase.auth.getSession().then(({ data }) => {
      const token = data.session?.access_token;
      const headers = new Headers(init?.headers);
      if (token) headers.set('Authorization', `Bearer ${token}`);
      return fetch(input, { ...init, headers });
    })) as typeof fetch;
}

/** Per-schema clients so `.from('manifest')` resolves to `harvest.manifest`, etc. */
export const supabaseHarvest: AnyClient = createClient(url ?? '', anonKey ?? '', {
  db: { schema: HARVEST_SCHEMA },
  auth: { persistSession: false },
  global: { fetch: makeAuthFetch() },
});

export const supabaseGeniusV2: AnyClient = createClient(url ?? '', anonKey ?? '', {
  db: { schema: GENIUS_V2_SCHEMA },
  auth: { persistSession: false },
  global: { fetch: makeAuthFetch() },
});

export const supabasePrestoV2: AnyClient = createClient(url ?? '', anonKey ?? '', {
  db: { schema: PRESTO_V2_SCHEMA },
  auth: { persistSession: false },
  global: { fetch: makeAuthFetch() },
});

/** Map a logical schema name to its client — used by the dynamic entity browser. */
export function clientForSchema(schema: string): AnyClient {
  switch (schema) {
    case GENIUS_V2_SCHEMA:
      return supabaseGeniusV2;
    case PRESTO_V2_SCHEMA:
      return supabasePrestoV2;
    case HARVEST_SCHEMA:
      return supabaseHarvest;
    default:
      throw new Error(
        `No Supabase client configured for schema "${schema}". Add it to src/lib/supabase.ts and the env config.`,
      );
  }
}
